// Per-client proposal config for Planet Life.
// Planet Life stays on WordPress; the work is a structured brand + website rebuild
// across Resort, Experiences, and Foundation.

export const planetLifeProposal = {
  slug: "planet-life",
  client: {
    name: "Planet Life",
    location: "Goa",
    sector: "Hospitality, experiences, and foundation"
  },
  meta: {
    projectTitle: "One clearer digital home for Planet Life.",
    docCode: "SN-PRO-PL-001",
    preparedBy: "Sutranode",
    preparedFor: "The Planet Life team",
    preparedDate: "May 2026",
    validityDays: 10
  },

  framing:
    "This proposal is built around the shape Planet Life already has: a parent brand, two Goa resorts, a compact Experiences offer, and a Foundation that should feel connected without being flattened into hospitality.",

  observed: {
    eyebrow: "What we observed",
    paragraphs: [
      "Planet Life is not a single-property website problem. It is a brand-architecture problem: Serendip, Nature's Nest, Experiences, and the Foundation each need their own space, but the visitor should still feel they are inside one thoughtful Planet Life world.",
      "The Resorts division has the highest commercial pressure because Serendip and Nature's Nest both have to help people choose, compare, enquire, and book. The Experiences division has a different job: one sharp page that makes offbeat travel feel curated rather than scattered.",
      "The Foundation needs restraint. It should not feel like an add-on to a resort website, and it should not overtake the booking journey. The right structure lets it add depth, credibility, and purpose to the parent brand while keeping guest decisions simple.",
      "Staying on WordPress is the right constraint for this engagement. The rebuild should improve structure, design, performance, editability, and conversion without forcing a platform migration the team has not asked for."
    ]
  },

  belief: {
    eyebrow: "What we believe",
    headline: "Planet Life needs a *system*, not a prettier set of pages.",
    body:
      "The website has to make the parent brand legible first, then let each division do its job. Resorts should convert. Experiences should intrigue. Foundation should build trust. WordPress can support that, provided the information architecture, templates, content model, and editorial discipline are designed before the page build begins."
  },

  pathsIntro: {
    eyebrow: "The three paths",
    headline: "Seed, Grove, and Canopy.",
    body:
      "Three WordPress rebuild scopes for a 15-20 page Planet Life site. Each tier gives you a complete website; the difference is how deeply we go into content, conversion design, editorial polish, and post-launch refinement."
  },

  tiers: [
    {
      id: "thrush",
      bird: "Seed",
      birdSub: "the clean foundation",
      birdNote:
        "A focused rebuild that gives Planet Life a clearer structure without expanding the engagement beyond the core website.",
      tagline: "A sharp WordPress rebuild for the parent brand and core pages.",
      price: 275000,
      priceLabel: "₹2,75,000",
      priceJustification: "Complete 15-20 page WordPress rebuild.",
      timeline: "3.5 months",
      bestFor: "If the priority is to replace the current site with a cleaner, faster, more coherent Planet Life presence.",
      includes: [
        "Discovery, sitemap, and page-level content plan",
        "15-20 page WordPress website structure",
        "Parent brand home page",
        "Resort division pages for Serendip and Nature's Nest",
        "Experiences single-page build",
        "Foundation overview page",
        "Homepage mock design showing typography, colors, structure, and section direction",
        "Mobile-responsive WordPress implementation",
        "Basic on-page SEO setup",
        "Launch QA and 14-day stabilisation"
      ],
      excludes: [
        "Deep copywriting across all pages",
        "Advanced booking or CRM automation",
        "Post-launch experimentation beyond stabilisation"
      ],
      twelveMonthOutcome:
        "Planet Life has one coherent website that the team can confidently share, update, and build on.",
      cta: "Start with Seed"
    },
    {
      id: "flycatcher",
      bird: "Grove",
      birdSub: "the recommended system",
      birdNote:
        "A stronger structure where each division has enough room to work, while the parent brand still holds everything together.",
      tagline: "A clearer WordPress site with better storytelling and a simpler enquiry flow.",
      price: 350000,
      priceLabel: "₹3,50,000",
      priceJustification: "Best balance of scope, polish, and timeline.",
      timeline: "4 months",
      bestFor:
        "If Planet Life needs the website to actively support resort enquiries, offbeat travel discovery, and parent-brand credibility.",
      mostChosen: true,
      includes: [
        "Everything in Seed, plus:",
        "Brand architecture refinement for Parent, Resorts, Experiences, and Foundation",
        "Copy refinement for key decision pages",
        "Dedicated resort detail layouts for Serendip and Nature's Nest",
        "Clear enquiry paths for Resorts and Experiences",
        "Foundation narrative page with restrained trust-building content",
        "Reusable WordPress blocks for future edits",
        "Analytics, event tracking, and conversion checkpoints",
        "Pre-launch SEO foundation for priority pages",
        "30-day post-launch optimisation window"
      ],
      excludes: [
        "Original photography or video production",
        "Long-form SEO content calendar",
        "Paid media setup or campaign management"
      ],
      twelveMonthOutcome:
        "The site does more than explain Planet Life; it helps the right visitor find the right division and take the next step.",
      cta: "Build with Grove"
    },
    {
      id: "hornbill",
      bird: "Canopy",
      birdSub: "the flagship presence",
      birdNote:
        "The full expression: a richer parent-brand experience, deeper resort storytelling, and a stronger foundation for future growth.",
      tagline: "A polished digital system for the next phase of Planet Life.",
      price: 430000,
      priceLabel: "₹4,30,000",
      priceJustification: "Expanded strategy, polish, and post-launch tuning.",
      timeline: "4.5 months",
      bestFor:
        "If the website is meant to become the long-term digital base for the full Planet Life ecosystem.",
      flagship: true,
      includes: [
        "Everything in Grove, plus:",
        "Expanded content direction across all 15-20 pages",
        "Premium visual direction for the parent brand and resort division",
        "Richer Serendip and Nature's Nest storytelling modules",
        "Experiences page designed as a compact campaign-style destination",
        "Foundation page structured for credibility, updates, and future initiatives",
        "Advanced performance, accessibility, and technical SEO pass",
        "60-day post-launch optimisation window",
        "Priority support during launch month"
      ],
      excludes: [
        "Third-party software license fees",
        "Paid media spend",
        "New photography, unless commissioned separately"
      ],
      twelveMonthOutcome:
        "Planet Life has a website that can carry the parent brand, sell the resorts, introduce Experiences, and make the Foundation feel integral.",
      cta: "Partner on Canopy"
    }
  ],

  recommendation: {
    hidden: false,
    eyebrow: "Our recommendation",
    headline: "For Planet Life, we'd build *Grove*.",
    body: [
      "Seed will solve the visible website problem: structure, design, responsive pages, and a cleaner WordPress build. It is the right choice if the brief is strictly to modernise the site and keep scope tight.",
      "Canopy is the right choice if Planet Life wants this to become a deeper brand platform, with more editorial polish, richer resort storytelling, stronger handover, and a longer optimisation window.",
      "Grove is the balanced recommendation. It gives enough strategic depth to handle the parent brand, both resorts, Experiences, and Foundation properly, without turning a 15-20 page WordPress rebuild into a brand programme that takes longer than the business needs."
    ]
  },

  process: {
    hidden: false,
    eyebrow: "How we'd work together",
    headline: "Five clear steps, from first call to launch.",
    phases: [
      {
        n: "01",
        title: "Discovery",
        weeks: "Weeks 1-2",
        line: "We align on priorities and map out the site."
      },
      {
        n: "02",
        title: "Structure",
        weeks: "Weeks 3-4",
        line: "We lock the sitemap and page-level direction."
      },
      {
        n: "03",
        title: "Design",
        weeks: "Weeks 5-8",
        line: "We share a homepage mock, then design the rest."
      },
      {
        n: "04",
        title: "Build",
        weeks: "Weeks 9-13",
        line: "We build everything in WordPress, responsive and SEO-ready."
      },
      {
        n: "05",
        title: "Launch",
        weeks: "Final 1-3 weeks",
        line: "We test, launch, and support you after go-live."
      }
    ]
  },

  tech: {
    eyebrow: "On the platform",
    headline: "We'd stay on WordPress.",
    body: [
      "A migration is not part of this recommendation. Planet Life already has a WordPress path, and the confirmed brief is to improve the website without changing the platform.",
      "The work is to make WordPress behave like a managed brand system: cleaner templates, reusable sections, sensible editing boundaries, better performance hygiene, and a structure the team can maintain after launch.",
      "The trade-off is discipline. WordPress can drift if every page becomes a one-off edit. We would set up a small set of reusable patterns and page rules so the site stays coherent after handover."
    ]
  },

  proof: {
    hidden: true,
    eyebrow: "Work that's structurally similar",
    note:
      "We pick proof by problem-shape: parent brand clarity, multi-offer navigation, conversion-led hospitality pages, and CMS handover."
  },

  next: {
    eyebrow: "What's next",
    headline: "Choose the scope, then we lock the build plan.",
    disableNavigation: true,
    note:
      "Once you say yes, we'll send the detailed work plan, milestones, and handover flow by email before the project starts.",
    primary: {
      label: "Let's get on a call",
      href: "https://cal.com/sutranode/proposal-planet-life"
    },
    secondary: {
      label: "I'm ready - let's start",
      action: "intent"
    },
    tertiary: {
      label: "Reply to the email this came in",
      action: "noop"
    }
  },

  notesAndSources: {
    eyebrow: "Notes & sources",
    note: "What this version is based on.",
    items: [
      "Confirmed Planet Life brief: parent brand with Resort, Experiences, and Foundation divisions.",
      "Resort division scope: Serendip and Nature's Nest, both in Goa.",
      "Experiences division scope: a single-page offbeat travel presence.",
      "Foundation scope: a non-profit arm that should feel connected to, but distinct from, the commercial journey.",
      "Confirmed platform decision: remain on WordPress; no migration.",
      "Confirmed commercial scope: 15-20 pages, three tiers, fixed prices, and timelines."
    ]
  },

  validity: {
    line:
      "This proposal is valid for 10 days from the prepared date. The numbers, scope, and timeline are based on the current brief; if circumstances change before signing, we'll revisit them together.",
    signature: "- Sachin, Sutranode"
  }
};
